import { useState, useMemo } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import SEOHead from '@/components/base/SEOHead';
import { categories, videos } from '@/mocks/data';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const currentSlug = slug || categories[0].slug;
  const [hoveredId, setHoveredId] = useState<number | null>(null);

  const activeCategory = categories.find((c) => c.slug === currentSlug) || categories[0];

  const filteredVideos = useMemo(() => {
    const results = videos.filter((v) => v.category === currentSlug);
    if (results.length > 0) return results;
    return videos.filter((v) => v.category === categories[0].slug);
  }, [currentSlug]);

  const categorySEO = {
    title: `蘑菇视频${activeCategory.name}频道 - 免费在线观看${activeCategory.name} | 蘑菇视频官网`,
    description: `蘑菇视频${activeCategory.name}频道为您提供海量${activeCategory.name}内容，高清画质流畅播放。免费观看最新最热${activeCategory.name}，支持多端同步，尽在蘑菇视频官网。`,
    keywords: `蘑菇视频${activeCategory.name},${activeCategory.name}在线观看,${activeCategory.name}免费,蘑菇视频官网,高清${activeCategory.name}`,
    canonicalUrl: `/category/${currentSlug}`,
  };

  const handleCategoryChange = (newSlug: string) => {
    navigate(`/category/${newSlug}`, { replace: true });
  };

  return (
    <>
      <SEOHead {...categorySEO} />
      <ScrollToTop />
      <Navbar />
      <main className="pt-16">
        <Breadcrumb />

        <section className="w-full px-4 md:px-6 py-8 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center">
                <i className={`${activeCategory.icon} text-primary-600 text-lg w-5 h-5 flex items-center justify-center`}></i>
              </div>
              <div>
                <h1 className="text-xl md:text-2xl font-heading text-foreground-950">
                  {activeCategory.name}
                </h1>
                <p className="text-sm text-foreground-500">{activeCategory.description}</p>
              </div>
            </div>

            <div className="flex items-center gap-2 mb-8 overflow-x-auto pb-2 flex-wrap">
              {categories.map((cat) => (
                <button
                  key={cat.slug}
                  onClick={() => handleCategoryChange(cat.slug)}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 cursor-pointer ${
                    currentSlug === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  {cat.name}
                </button>
              ))}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
              {filteredVideos.map((video) => (
                <Link
                  key={video.id}
                  to={`/article/${video.id}`}
                  className="group bg-background-100 rounded-xl overflow-hidden hover:shadow-sm transition-all duration-300 cursor-pointer"
                  onMouseEnter={() => setHoveredId(video.id)}
                  onMouseLeave={() => setHoveredId(null)}
                >
                  <div className="relative aspect-video overflow-hidden">
                    <img
                      src={video.image}
                      alt={video.title}
                      className={`w-full h-full object-cover transition-transform duration-500 ${
                        hoveredId === video.id ? 'scale-110' : 'scale-100'
                      }`}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                    <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between">
                      <span className="px-2 py-1 rounded-md bg-background-50/20 backdrop-blur-sm text-background-50 text-xs font-medium whitespace-nowrap">
                        {video.duration}
                      </span>
                      <span className="px-2 py-1 rounded-md bg-background-50/20 backdrop-blur-sm text-background-50 text-xs font-medium whitespace-nowrap">
                        {video.views}次播放
                      </span>
                    </div>
                    <div className={`absolute inset-0 bg-black/30 flex items-center justify-center transition-opacity duration-300 ${
                      hoveredId === video.id ? 'opacity-100' : 'opacity-0'
                    }`}>
                      <div className="w-14 h-14 rounded-full bg-primary-500 flex items-center justify-center">
                        <i className="ri-play-fill text-background-50 text-2xl w-6 h-6 flex items-center justify-center ml-0.5"></i>
                      </div>
                    </div>
                  </div>
                  <div className="p-4">
                    <h3 className="text-sm font-semibold text-foreground-950 mb-1.5 line-clamp-1 group-hover:text-primary-600 transition-colors">
                      {video.title}
                    </h3>
                    <p className="text-xs text-foreground-500 line-clamp-2 mb-3">{video.description}</p>
                    <div className="flex items-center gap-2">
                      <span className="flex items-center gap-1 text-xs text-amber-500">
                        <i className="ri-star-fill w-3 h-3 flex items-center justify-center"></i>
                        {video.rating}
                      </span>
                      <span className="text-foreground-400 text-xs">|</span>
                      <span className="text-xs text-foreground-500 whitespace-nowrap">{video.date}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            {filteredVideos.length === 0 && (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
                  <i className="ri-inbox-line text-foreground-400 text-2xl w-6 h-6 flex items-center justify-center"></i>
                </div>
                <p className="text-foreground-500">该分类暂无内容，请浏览其他分类</p>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}